import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { ArtifactStatusChip } from '@verantos-dev/shared-components';
import { fontWeight } from '@verantos-dev/shared-ui-components/tokens';

import type { SourceCohortDisplay } from '../types';
import { toAssetStatus } from '../utils/assetStatusLabel';
import { getSubjectCountLabel } from '../utils/subjectCountLabel';

// The cohort's name with its status right beside it, then its size and its action in
// columns that line up from one cohort to the next. Narrow widths keep the name, its
// status and the action, and drop the size.
const ROW_COLUMNS = {
  xs: 'minmax(0, 1fr) auto',
  md: 'minmax(0, 1fr) minmax(0, 128px) 40px',
} as const;

interface SourceCohortRowProps {
  cohort: SourceCohortDisplay;
  /** The cohort's own page: followed by a modified click (new tab/window). */
  href: string;
  staleTooltip: string;
  switchLabel: string;
  onSwitch: () => void;
  isSwitchDisabled: boolean;
  /** Why switching is unavailable (shown on the disabled button), when it is. */
  switchDisabledTooltip?: string;
}

// A source cohort in the cohorts card. The switch action stands in until the card
// has its own add and per-cohort actions.
export const SourceCohortRow = ({
  cohort,
  href,
  staleTooltip,
  switchLabel,
  onSwitch,
  isSwitchDisabled,
  switchDisabledTooltip,
}: SourceCohortRowProps) => (
  <Box
    data-testid={`analysis-source-cohort-row-${cohort.cohortProfileId}`}
    sx={{
      display: 'grid',
      gridTemplateColumns: ROW_COLUMNS,
      alignItems: 'center',
      columnGap: 3,
      px: 4,
      py: 1.5,
    }}
  >
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}
      data-testid={`analysis-source-cohort-identity-${cohort.cohortProfileId}`}
    >
      {/* A real anchor: a plain click navigates in-app via the router, while
          Cmd/Ctrl/Shift+click and middle-click open the cohort in a new tab/window. */}
      <Link
        component={RouterLink}
        to={href}
        underline="hover"
        noWrap
        title={cohort.cohortName}
        sx={{
          typography: 'body1',
          fontWeight: fontWeight.medium,
          color: 'primary.main',
          minWidth: 0,
        }}
        data-testid={`analysis-source-cohort-link-${cohort.cohortProfileId}`}
      >
        {cohort.cohortName}
      </Link>
      {/* flexShrink 0: a long name is truncated, not the status beside it. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <ArtifactStatusChip
          status={toAssetStatus(cohort.status)}
          data-testid={`analysis-source-cohort-status-${cohort.cohortProfileId}`}
        />
        {cohort.isStale && (
          <Tooltip title={staleTooltip} arrow>
            <WarningIcon
              fontSize="small"
              color="warning"
              data-testid={`analysis-source-cohort-stale-${cohort.cohortProfileId}`}
            />
          </Tooltip>
        )}
      </Box>
    </Box>

    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ display: { xs: 'none', md: 'block' } }}
      data-testid={`analysis-source-cohort-subjects-${cohort.cohortProfileId}`}
    >
      {getSubjectCountLabel(cohort.subjectCount)}
    </Typography>

    {/* span wrapper so the tooltip still shows while the button is disabled */}
    <Tooltip title={switchDisabledTooltip ?? switchLabel}>
      <span>
        <IconButton
          size="small"
          onClick={onSwitch}
          disabled={isSwitchDisabled}
          aria-label={switchLabel}
          data-testid="analysis-source-cohort-switch"
        >
          <SwapHorizIcon fontSize="small" color={isSwitchDisabled ? 'disabled' : 'primary'} />
        </IconButton>
      </span>
    </Tooltip>
  </Box>
);
